import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { base44 } from '../api/apiClient';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(() => (
    !!localStorage.getItem('impersonator_access_token')
  ));
  const [activeEmployeeId, setActiveEmployeeIdState] = useState(() => (
    localStorage.getItem('active_employee_id') || ''
  ));

  const fetchUser = useCallback(async () => {
    const access = localStorage.getItem('access_token');
    if (!access) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/me/`, {
        headers: { Authorization: `Bearer ${access}` },
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshToken = async () => {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) return false;
    try {
      const response = await axios.post(`${API_URL}/auth/refresh/`, { refresh });
      localStorage.setItem('access_token', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refresh_token', response.data.refresh);
      }
      const userResponse = await axios.get(`${API_URL}/auth/me/`, {
        headers: { Authorization: `Bearer ${response.data.access}` },
      });
      setUser(userResponse.data);
      return true;
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      return false;
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/auth/login/`, { username, password });
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    localStorage.removeItem('impersonator_access_token');
    localStorage.removeItem('impersonator_refresh_token');
    setIsImpersonating(false);
    const userData = await fetchUser();
    return userData;
  };

  const startImpersonation = async (userId) => {
    const currentAccess = localStorage.getItem('access_token');
    const currentRefresh = localStorage.getItem('refresh_token');
    if (!currentAccess || !currentRefresh) {
      throw new Error('Missing admin session.');
    }

    const response = await axios.post(
      `${API_URL}/auth/impersonate/${userId}/`,
      {},
      { headers: { Authorization: `Bearer ${currentAccess}` } },
    );
    localStorage.setItem('impersonator_access_token', currentAccess);
    localStorage.setItem('impersonator_refresh_token', currentRefresh);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    localStorage.removeItem('current_restaurant_id');
    localStorage.removeItem('active_employee_id');
    setActiveEmployeeIdState('');
    setIsImpersonating(true);
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register-org/`, data);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    localStorage.removeItem('impersonator_access_token');
    localStorage.removeItem('impersonator_refresh_token');
    setIsImpersonating(false);
    await fetchUser();
    return response.data;
  };

  const registerWithInvite = async (token, data) => {
    const response = await axios.post(`${API_URL}/auth/register/invite/${token}/`, data);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    localStorage.removeItem('impersonator_access_token');
    localStorage.removeItem('impersonator_refresh_token');
    setIsImpersonating(false);
    await fetchUser();
    return response.data;
  };

  const logout = async () => {
    const adminAccess = localStorage.getItem('impersonator_access_token');
    const adminRefresh = localStorage.getItem('impersonator_refresh_token');
    if (adminAccess && adminRefresh) {
      localStorage.setItem('access_token', adminAccess);
      localStorage.setItem('refresh_token', adminRefresh);
      localStorage.removeItem('impersonator_access_token');
      localStorage.removeItem('impersonator_refresh_token');
      localStorage.removeItem('current_restaurant_id');
      localStorage.removeItem('active_employee_id');
      setActiveEmployeeIdState('');
      setIsImpersonating(false);
      await fetchUser();
      return { restoredAdmin: true };
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('impersonator_access_token');
    localStorage.removeItem('impersonator_refresh_token');
    localStorage.removeItem('current_restaurant_id');
    localStorage.removeItem('active_employee_id');
    setActiveEmployeeIdState('');
    setIsImpersonating(false);
    setUser(null);
    return { restoredAdmin: false };
  };

  const completeOnboarding = async () => {
    // Route through apiClient so the response interceptor refreshes the
    // access token if it expired during a long onboarding session.
    const data = await base44.auth.completeOnboarding();
    setUser(data);
    return data;
  };

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const profiles = user?.shared_employee_profiles || [];
    if (!user?.is_shared_account || profiles.length === 0) {
      localStorage.removeItem('active_employee_id');
      setActiveEmployeeIdState('');
      return;
    }
    const valid = profiles.some((employee) => String(employee.id) === String(activeEmployeeId));
    if (!valid) {
      const nextId = String(profiles[0].id);
      localStorage.setItem('active_employee_id', nextId);
      setActiveEmployeeIdState(nextId);
    }
  }, [user, activeEmployeeId]);

  const setActiveEmployeeId = (employeeId) => {
    const nextId = employeeId ? String(employeeId) : '';
    if (nextId) {
      localStorage.setItem('active_employee_id', nextId);
    } else {
      localStorage.removeItem('active_employee_id');
    }
    setActiveEmployeeIdState(nextId);
  };

  // Role helpers
  const isOrgAdmin = user?.role === 'org_admin';
  const isRestaurantAdmin = user?.role === 'restaurant_admin';
  const isStaff = user?.role === 'staff';
  const isAdmin = isOrgAdmin || isRestaurantAdmin;
  const isSuperadmin = user?.is_superadmin === true;
  const sharedEmployeeProfiles = user?.shared_employee_profiles || [];
  const activeEmployee = sharedEmployeeProfiles.find(
    (employee) => String(employee.id) === String(activeEmployeeId),
  ) || null;

  const hasModuleAccess = (moduleKey, requiredLevel = 'view') => {
    if (!user) return false;
    if (isSuperadmin || isOrgAdmin || isRestaurantAdmin) return true;
    const level = user.module_permissions?.[moduleKey] || 'none';
    if (requiredLevel === 'view') return level === 'view' || level === 'edit';
    if (requiredLevel === 'edit') return level === 'edit';
    return false;
  };

  return (
    <AuthContext.Provider value={{
      user, isAdmin, isOrgAdmin, isRestaurantAdmin, isStaff, isSuperadmin,
      loading, login, register, registerWithInvite, logout, fetchUser, completeOnboarding, hasModuleAccess,
      startImpersonation, isImpersonating,
      activeEmployeeId, activeEmployee, setActiveEmployeeId,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
